import * as React from "react"
import { Profile } from "../../profile.ts"
import type { CurrentPlaylist, CurrentTrack } from "./audio-player-context.tsx"
import { AudioPlayerContext } from "./audio-player-context.tsx"
import { AudioPlayerDrawer } from "./audio-player-drawer.tsx"

const AUDIO_ELEMENT_ID = "playlist-audio-player"
const TIME_UPDATE_INTERVAL = 1000 // 1 second in milliseconds

export const AudioPlayerProvider = ({ children }: React.PropsWithChildren) => {
  const audioRef = React.useRef<HTMLAudioElement>(null)

  return (
    <React.Fragment>
      <audio ref={audioRef} id={AUDIO_ELEMENT_ID} hidden preload="metadata" />
      <Provider audioRef={audioRef}>{children}</Provider>
    </React.Fragment>
  )
}

function Provider({
  children,
  audioRef,
}: React.PropsWithChildren<{ audioRef: React.RefObject<HTMLAudioElement | null> }>) {
  const currentPlaylistRef = React.useRef<CurrentPlaylist | null>(null)
  const animationFrameRef = React.useRef<number | null>(null)
  const lastTimeUpdateTimeRef = React.useRef<number>(0)

  const [isPlaying, setIsPlaying] = React.useState(false)
  const [currentTrack, setCurrentTrack] = React.useState<CurrentTrack | null>(null)
  const [currentTime, setCurrentTime] = React.useState<number>(0)

  const _updateCurrentTime = (time: number) => {
    if (!audioRef.current) return
    audioRef.current.currentTime = time
    setCurrentTime(time)
  }

  const _resetCurrentTime = () => {
    _updateCurrentTime(0)
  }

  const _endCurrentTrack = () => {
    if (!audioRef.current) return
    _updateCurrentTime(audioRef.current.duration)
  }

  const _tickTimeUpdate = (timestamp: number) => {
    if (!audioRef.current) return

    if (
      !lastTimeUpdateTimeRef.current ||
      timestamp - lastTimeUpdateTimeRef.current >= TIME_UPDATE_INTERVAL
    ) {
      setCurrentTime(audioRef.current.currentTime)
    }

    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(_tickTimeUpdate)
    }
  }

  const _pauseAllOtherAudio = () => {
    const audioElements = document.querySelectorAll("audio")
    audioElements.forEach((audio) => {
      if (audio.id !== AUDIO_ELEMENT_ID) {
        audio.pause()
      }
    })
  }

  const _isTrackReleased = (track: Profile.Track) => {
    return Profile.Track.$is("Released")(track)
  }

  const _playTrack = (track: Profile.ReleasedTrack, playlist: CurrentPlaylist) => {
    if (!audioRef.current) return
    const index = playlist.tracks.findIndex((t) => t.id === track.id)
    if (index === -1) return

    setCurrentTrack({
      id: track.id,
      title: track.title,
      artist: track.artist,
      duration: track.duration,
      playlist: { id: playlist.id, coverImageUrl: playlist.coverImageUrl },
      index,
    })

    currentPlaylistRef.current = playlist
    audioRef.current.src = track.playbackUrl

    _resetCurrentTime()
    _pauseAllOtherAudio()

    audioRef.current.play()
  }

  const playPlaylist = (playlist: Profile.Playlist) => {
    const releasedTracks = playlist.tracks.filter((t) => _isTrackReleased(t))
    const firstReleasedTrack = releasedTracks[0]
    if (!firstReleasedTrack) return

    _playTrack(firstReleasedTrack, {
      id: playlist.id,
      coverImageUrl: playlist.coverImageUrl,
      tracks: releasedTracks,
    })
  }

  const playTrack = (trackId: string, playlist: Profile.Playlist) => {
    const releasedTracks = playlist.tracks.filter((t) => _isTrackReleased(t))
    const track = releasedTracks.find((t) => t.id === trackId)

    if (track) {
      _playTrack(track, {
        id: playlist.id,
        coverImageUrl: playlist.coverImageUrl,
        tracks: releasedTracks,
      })
    }
  }

  const playNextTrack = () => {
    if (!audioRef.current || !currentTrack) return
    const currentPlaylist = currentPlaylistRef.current
    if (!currentPlaylist) return

    const nextTrack = currentPlaylist.tracks[currentTrack.index + 1]
    if (nextTrack) {
      _playTrack(nextTrack, currentPlaylist)
    } else {
      if (!audioRef.current.ended) {
        _endCurrentTrack()
      }
    }
  }

  const playPreviousTrack = () => {
    if (!currentTrack) return
    const currentPlaylist = currentPlaylistRef.current
    if (!currentPlaylist) return
    const previousTrack = currentPlaylist.tracks[currentTrack.index - 1]
    if (previousTrack) {
      _playTrack(previousTrack, currentPlaylist)
    } else {
      _resetCurrentTime()
    }
  }

  const togglePlayPause = () => {
    if (!audioRef.current) return
    if (audioRef.current.paused) {
      if (!currentTrack) return
      audioRef.current.play()
    } else {
      audioRef.current.pause()
    }
  }

  React.useEffect(() => {
    if (isPlaying && audioRef.current) {
      _tickTimeUpdate(performance.now())
    } else if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [isPlaying])

  React.useEffect(() => {
    if (!audioRef.current) return

    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)
    const handleEnded = () => playNextTrack()

    audioRef.current.addEventListener("play", handlePlay)
    audioRef.current.addEventListener("pause", handlePause)
    audioRef.current.addEventListener("ended", handleEnded)

    return () => {
      audioRef.current?.removeEventListener("play", handlePlay)
      audioRef.current?.removeEventListener("pause", handlePause)
      audioRef.current?.removeEventListener("ended", handleEnded)
    }
  }, [])

  return (
    <AudioPlayerContext.Provider
      value={{
        currentTrack,
        currentTrackTime: currentTime,
        isPlaying,
        playTrack,
        playPlaylist,
        playNextTrack,
        playPreviousTrack,
        togglePlayPause,
        setCurrentTrackTime: _updateCurrentTime,
      }}
    >
      <audio
        ref={audioRef}
        id={AUDIO_ELEMENT_ID}
        hidden
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => playNextTrack()}
      />
      <AudioPlayerDrawer />
      {children}
    </AudioPlayerContext.Provider>
  )
}
